// Web Share — share a plant card image + summary.
// Falls back to clipboard copy of the summary if Web Share is unavailable.

const dataUrlToBlob = (dataUrl) => {
  const m = /^data:(.+?);base64,(.+)$/.exec(dataUrl);
  if (!m) return null;
  const [, mime, b64] = m;
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
};

const renderShareCard = async ({ imageDataUrl, title, subtitle, lines, state }) => {
  // Compose a 1080x1350 portrait card.
  const W = 1080, H = 1350;
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d");

  // Background
  ctx.fillStyle = "#FDFBF7";
  ctx.fillRect(0, 0, W, H);

  // Top photo (square)
  if (imageDataUrl) {
    const img = await new Promise((res, rej) => {
      const im = new Image();
      im.crossOrigin = "anonymous";
      im.onload = () => res(im);
      im.onerror = rej;
      im.src = imageDataUrl;
    }).catch(() => null);
    if (img) {
      const side = W;
      const scale = Math.max(side / img.width, side / img.height);
      const dw = img.width * scale, dh = img.height * scale;
      ctx.drawImage(img, (W - dw) / 2, 0, dw, dh > side ? side : dh);
    }
  }

  // Dark gradient bottom for readability of overlaid text
  const grad = ctx.createLinearGradient(0, W - 200, 0, W);
  grad.addColorStop(0, "rgba(0,0,0,0)");
  grad.addColorStop(1, "rgba(0,0,0,0.55)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, W - 200, W, 200);

  // State pill (top-left of photo)
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  const pillW = 240, pillH = 56;
  ctx.beginPath();
  ctx.roundRect(40, 40, pillW, pillH, 28);
  ctx.fill();
  ctx.fillStyle = "#2D4A22";
  ctx.font = "600 22px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.textBaseline = "middle";
  ctx.textAlign = "center";
  ctx.fillText(`PRAIRIE · ${state || ""}`, 40 + pillW / 2, 40 + pillH / 2);

  // Title (below photo)
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#1B231C";
  ctx.font = "700 72px Cormorant Garamond, Georgia, serif";
  ctx.fillText(title || "", 60, W + 100);

  if (subtitle) {
    ctx.fillStyle = "#5F6E5C";
    ctx.font = "italic 30px Cormorant Garamond, Georgia, serif";
    ctx.fillText(subtitle, 60, W + 145);
  }

  // Bullet lines
  ctx.fillStyle = "#2D4A22";
  ctx.font = "500 28px system-ui, -apple-system, Segoe UI, sans-serif";
  let y = W + 220;
  for (const line of lines) {
    if (y > H - 80) break;
    ctx.fillText(`•  ${line}`, 60, y);
    y += 44;
  }

  // Footer
  ctx.fillStyle = "#8a8a83";
  ctx.font = "500 22px system-ui, -apple-system, Segoe UI, sans-serif";
  ctx.fillText("Identified with Prairie", 60, H - 50);

  return new Promise((res) => c.toBlob((b) => res(b), "image/jpeg", 0.92));
};

export async function sharePlant({ scan, id, state, summary, t }) {
  const lines = [];
  if (id.is_state_native === true) lines.push(t("share.linesNative", { state }));
  if (id.is_state_native === false) lines.push(t("share.linesNonNative", { state }));
  if ((id.plant_type || "").toLowerCase() === "beneficial") lines.push(t("share.linesBeneficial"));
  if ((id.plant_type || "").toLowerCase() === "weed") lines.push(t("share.linesWeed"));
  if (id.is_keystone) lines.push(t("share.linesKeystone"));
  if (id.endangered_or_threatened_in_state) lines.push(t("share.linesEndangered", { state }));
  if (id.sun_preference?.length) lines.push(t("share.linesSun", { sun: id.sun_preference.join(", ") }));

  const title = id.common_name || t("pdf.unknown");
  const subtitle = id.scientific_name || "";

  const blob = await renderShareCard({
    imageDataUrl: scan.image_base64,
    title, subtitle, lines, state,
  });

  const text = [`${title}${subtitle ? ` (${subtitle})` : ""}`, ...lines, t("share.footer")].join("\n");

  // Try native Web Share with a file first
  if (blob && navigator.canShare && navigator.share) {
    const file = new File([blob], "prairie-plant.jpg", { type: "image/jpeg" });
    const payload = { title: "Prairie", text, files: [file] };
    if (navigator.canShare(payload)) {
      try { await navigator.share(payload); return { ok: true, mode: "files" }; } catch (e) { /* user cancelled */ if (e?.name === "AbortError") return { ok: false, mode: "abort" }; }
    }
  }
  // Fallback 1: share text
  if (navigator.share) {
    try { await navigator.share({ title: "Prairie", text }); return { ok: true, mode: "text" }; } catch (e) { if (e?.name === "AbortError") return { ok: false, mode: "abort" }; }
  }
  // Fallback 2: clipboard
  try {
    await navigator.clipboard.writeText(text);
    return { ok: true, mode: "clipboard" };
  } catch (e) {
    if (process.env.NODE_ENV !== "production") console.warn("[share] clipboard fallback failed:", e);
  }
  // Fallback 3: download the card
  if (blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "prairie-plant.jpg";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    return { ok: true, mode: "download" };
  }
  return { ok: false, mode: "none" };
}

export { dataUrlToBlob };
